import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { Toaster } from "./components/ui/sonner";

// Layout
import { Layout } from "./components/Layout";

// Pages
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import ProductList from "./pages/ProductList";
import ProductDetail from "./pages/ProductDetail";
import ProductForm from "./pages/ProductForm";
import UserManagement from "./pages/UserManagement";
import DataImport from "./pages/DataImport";
import Settings from "./pages/Settings";
import QuoteList from "./pages/QuoteList";
import QuoteBuilder from "./pages/QuoteBuilder";
import QuotePreview from "./pages/QuotePreview";
import CustomProductList from "./pages/CustomProductList";
import CustomProductForm from "./pages/CustomProductForm";
import CategoryManagement from "./pages/CategoryManagement";
import RolePermissions from "./pages/RolePermissions";
import PermissionSettings from "./pages/PermissionSettings";
import SheetOptimizer from "./pages/SheetOptimizer";
import ChallanList from "./pages/ChallanList";
import ChallanBuilder from "./pages/ChallanBuilder";
import ChallanPreview from "./pages/ChallanPreview";
import ImageImport from "./pages/ImageImport";
import CustomerList from "./pages/CustomerList";
import CustomerDetail from "./pages/CustomerDetail";
import CustomerMigration from "./pages/CustomerMigration";
import ConfigManager from "./pages/ConfigManager";

// Protected Route wrapper
function ProtectedRoute({ children }) {
    const { user, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
            </div>
        );
    }
    
    if (!user) {
        return <Navigate to="/login" replace />;
    }
    
    return <Layout>{children}</Layout>;
}

// Public Route wrapper (redirects if logged in)
function PublicRoute({ children }) {
    const { user, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
            </div>
        );
    }
    
    if (user) {
        return <Navigate to="/" replace />;
    }
    
    return children;
}

function AppRoutes() {
    return (
        <Routes>
            {/* Public routes */}
            <Route path="/login" element={
                <PublicRoute>
                    <Login />
                </PublicRoute>
            } />
            
            {/* Protected routes */}
            <Route path="/" element={
                <ProtectedRoute>
                    <Dashboard />
                </ProtectedRoute>
            } />
            
            <Route path="/products" element={
                <ProtectedRoute>
                    <ProductList />
                </ProtectedRoute>
            } />
            
            <Route path="/products/new" element={
                <ProtectedRoute>
                    <ProductForm />
                </ProtectedRoute>
            } />
            
            <Route path="/products/:productKey" element={
                <ProtectedRoute>
                    <ProductDetail />
                </ProtectedRoute>
            } />
            
            <Route path="/products/:productKey/edit" element={
                <ProtectedRoute>
                    <ProductForm />
                </ProtectedRoute>
            } />
            
            <Route path="/users" element={
                <ProtectedRoute>
                    <UserManagement />
                </ProtectedRoute>
            } />
            
            <Route path="/import" element={
                <ProtectedRoute>
                    <DataImport />
                </ProtectedRoute>
            } />
            
            <Route path="/import/images" element={
                <ProtectedRoute>
                    <ImageImport />
                </ProtectedRoute>
            } />
            
            <Route path="/settings" element={
                <ProtectedRoute>
                    <Settings />
                </ProtectedRoute>
            } />

            {/* Quotation Engine */}
            <Route path="/quotes" element={
                <ProtectedRoute>
                    <QuoteList />
                </ProtectedRoute>
            } />
            <Route path="/quotes/new" element={
                <ProtectedRoute>
                    <QuoteBuilder />
                </ProtectedRoute>
            } />
            <Route path="/quotes/:quoteId" element={
                <ProtectedRoute>
                    <QuoteBuilder />
                </ProtectedRoute>
            } />
            <Route path="/quotes/:quoteId/preview" element={
                <ProtectedRoute>
                    <QuotePreview />
                </ProtectedRoute>
            } />

            {/* Custom Products */}
            <Route path="/custom-products" element={
                <ProtectedRoute>
                    <CustomProductList />
                </ProtectedRoute>
            } />
            <Route path="/custom-products/new" element={
                <ProtectedRoute>
                    <CustomProductForm />
                </ProtectedRoute>
            } />
            <Route path="/custom-products/:productId/edit" element={
                <ProtectedRoute>
                    <CustomProductForm />
                </ProtectedRoute>
            } />

            {/* Customers */}
            <Route path="/customers" element={
                <ProtectedRoute>
                    <CustomerList />
                </ProtectedRoute>
            } />
            <Route path="/customers/new" element={
                <ProtectedRoute>
                    <CustomerDetail />
                </ProtectedRoute>
            } />
            <Route path="/customers/migrate" element={
                <ProtectedRoute>
                    <CustomerMigration />
                </ProtectedRoute>
            } />
            <Route path="/customers/:id" element={
                <ProtectedRoute>
                    <CustomerDetail />
                </ProtectedRoute>
            } />

            {/* Admin */}
            <Route path="/admin/categories" element={
                <ProtectedRoute>
                    <CategoryManagement />
                </ProtectedRoute>
            } />
            <Route path="/admin/roles" element={
                <ProtectedRoute>
                    <RolePermissions />
                </ProtectedRoute>
            } />
            <Route path="/admin/permissions" element={
                <ProtectedRoute>
                    <PermissionSettings />
                </ProtectedRoute>
            } />
            <Route path="/admin/config" element={
                <ProtectedRoute>
                    <ConfigManager />
                </ProtectedRoute>
            } />
            <Route path="/sheet-optimizer" element={
                <ProtectedRoute>
                    <SheetOptimizer />
                </ProtectedRoute>
            } />
            <Route path="/challans" element={
                <ProtectedRoute>
                    <ChallanList />
                </ProtectedRoute>
            } />
            <Route path="/challans/new" element={
                <ProtectedRoute>
                    <ChallanBuilder />
                </ProtectedRoute>
            } />
            <Route path="/challans/:challanId" element={
                <ProtectedRoute>
                    <ChallanBuilder />
                </ProtectedRoute>
            } />
            <Route path="/challans/:challanId/preview" element={
                <ProtectedRoute>
                    <ChallanPreview />
                </ProtectedRoute>
            } />
            
            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <AppRoutes />
                <Toaster position="top-right" richColors />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
